/**
 * CMS visual-edit bridge (child side).
 *
 * Lives inside a client website. When the site is loaded inside the CoreTechs
 * Studio preview iframe with `?cmsEdit=1`, this bridge:
 *   1. announces itself to the parent (Studio) and reports its pages/location,
 *   2. exposes an "edit enabled" flag the UI uses to turn fields editable,
 *   3. posts field changes up to the parent (which saves them server-side),
 *   4. accepts the saved section data back so the page re-renders live,
 *   5. keeps navigation inside the iframe while editing (so links that open
 *      in a new tab on the public site don't escape the preview),
 *   6. lets the parent drive navigation and language.
 *
 * It is framework-agnostic (no React) and self-contained so it can be copied
 * into any client site. The only site integration points are:
 *   - mounting <CmsProvider> (CmsContext.tsx),
 *   - letting useJsonData read getSectionOverride() for live updates.
 *
 * Message envelope (both directions): { source, type, ... }
 *   child → parent:  source "cms-child"
 *     "cms:ready" | "cms:change" {section,path,lang,value}
 *     "cms:location" {path} | "cms:pages" {pages}
 *   parent → child:  source "cms-parent"
 *     "cms:editmode" {on} | "cms:data" {section,data}
 *     "cms:navigate" {path} | "cms:setLanguage" {lang}
 */

export interface CmsChange {
  section: string;
  path: string;
  lang: string;
  value: unknown;
}

export type CmsMutationOp = "add" | "remove" | "move";

export interface CmsMutation {
  section: string;
  lang: string;
  op: CmsMutationOp;
  /** Dot-path to the array being mutated, e.g. "sections.0.items". */
  path: string;
  index?: number;
  toIndex?: number;
  value?: unknown;
}

export interface CmsAddRequest {
  section: string;
  lang: string;
  /** Dot-path to the array a new item should be appended to. */
  path: string;
  /** Human label for the modal title (e.g. "Add dish"). */
  label?: string;
  /** Fallback blank item used when the section has no JSON schema. */
  template?: unknown;
}

export interface CmsEditRequest {
  section: string;
  lang: string;
  /** Dot-path to the array containing the item. */
  path: string;
  /** Index of the item to edit within that array. */
  index: number;
  /** Human label for the modal title (e.g. "Edit photo"). */
  label?: string;
}

export interface CmsPage {
  path: string;
  label: string;
  /** True when the page has been wired for editing (declared in EDITABLE_PAGES). */
  editable?: boolean;
}

type Listener = () => void;

const CHILD_SOURCE = "cms-child";
const PARENT_SOURCE = "cms-parent";

let initialized = false;
let editEnabled = false;
let allowedOrigins: string[] = [];
const overrides = new Map<string, unknown>();
const listeners = new Set<Listener>();

// Cached so we can re-announce when the parent (re)activates edit mode.
let cachedPages: CmsPage[] | null = null;
let cachedPath: string | null = null;

let languageSetter: ((lang: string) => void) | null = null;

function emit(): void {
  listeners.forEach((listener) => listener());
}

function isFramed(): boolean {
  try {
    return typeof window !== "undefined" && window.parent && window.parent !== window;
  } catch {
    // Cross-origin access throw means we are framed by a different origin.
    return true;
  }
}

function isLocalDevStudioOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    return hostname === "localhost" || hostname === "127.0.0.1";
  } catch {
    return false;
  }
}

function originAllowed(origin: string): boolean {
  if (allowedOrigins.includes("*")) return true;
  if (allowedOrigins.includes(origin)) return true;
  // Local CoreTechs Studio (`npm run dev`) framing this site — saves still go through Studio JWT.
  return isLocalDevStudioOrigin(origin);
}

function postToParent(type: string, payload: Record<string, unknown>): void {
  if (!isFramed()) return;
  // Use '*' so localhost Studio receives cms:ready. Studio validates event.origin.
  window.parent.postMessage({ source: CHILD_SOURCE, type, ...payload }, "*");
}

export function subscribeCms(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isCmsEditEnabled(): boolean {
  return editEnabled;
}

/** True when running inside another window (the Studio preview iframe). */
export function isCmsFramed(): boolean {
  return isFramed();
}

export function getSectionOverride(section: string): unknown {
  return overrides.has(section) ? overrides.get(section) : undefined;
}

export function reportChange(change: CmsChange): void {
  postToParent("cms:change", { ...change });
}

/** Report an array mutation (add/remove/reorder) for the parent to persist. */
export function reportMutation(mutation: CmsMutation): void {
  postToParent("cms:mutate", { ...mutation });
}

/** Ask the parent to open its schema-driven form to create a new item. */
export function requestAdd(request: CmsAddRequest): void {
  postToParent("cms:addRequest", { ...request });
}

/** Ask the parent to open its schema-driven form to edit an existing item
 * (e.g. to replace an image via the schema's image picker). */
export function requestEdit(request: CmsEditRequest): void {
  postToParent("cms:editRequest", { ...request });
}

/** Report the routes the editor can jump to (rendered as a page selector). */
export function reportPages(pages: CmsPage[]): void {
  cachedPages = pages;
  postToParent("cms:pages", { pages });
}

/** Report the current in-frame location so the parent stays in sync. */
export function reportLocation(path: string): void {
  cachedPath = path;
  postToParent("cms:location", { path });
}

/** Let the React layer apply a language change requested by the parent. */
export function registerCmsLanguageSetter(fn: (lang: string) => void): void {
  languageSetter = fn;
}

/** Keep ?cmsEdit=1 across in-frame SPA navigations; report a clean path to Studio. */
function resolveNavigateUrl(path: string): { href: string; reportPath: string } {
  const next = new URL(path, window.location.href);
  if (new URLSearchParams(window.location.search).get("cmsEdit") === "1") {
    next.searchParams.set("cmsEdit", "1");
  }
  const href = next.pathname + next.search + next.hash;
  const reportParams = new URLSearchParams(next.search);
  reportParams.delete("cmsEdit");
  const qs = reportParams.toString();
  const reportPath = next.pathname + (qs ? `?${qs}` : "") + next.hash;
  return { href, reportPath };
}

/** Same-frame SPA navigation. Commits the focused field first, then routes via
 * the History API (react-router picks up the popstate). */
function navigateInFrame(path: string): void {
  const active = document.activeElement as HTMLElement | null;
  if (active && active.getAttribute("data-cms-editable") === "true") {
    active.blur();
  }
  const { href, reportPath } = resolveNavigateUrl(path);
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
  reportLocation(reportPath);
}

function sameOriginPath(href: string): string | null {
  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return null;
    // Pure in-page hash/anchor → let the browser handle it.
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) {
      return null;
    }
    return url.pathname + url.search;
  } catch {
    return null;
  }
}

function installNavigationGuards(): void {
  // Keep internal navigation inside the frame while editing.
  document.addEventListener(
    "click",
    (event) => {
      if (!editEnabled) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
      const target = event.target as HTMLElement | null;
      if (!target || target.closest('[data-cms-editable="true"]')) return;
      const anchor = target.closest("a");
      if (!anchor) return;
      const path = sameOriginPath(anchor.href);
      if (path === null) return; // external link → leave alone
      event.preventDefault();
      navigateInFrame(path);
    },
    true
  );

  // Block form submissions (e.g. contact/booking) while editing.
  document.addEventListener(
    "submit",
    (event) => {
      if (!editEnabled) return;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  // Route same-origin window.open() calls back into the frame while editing.
  const nativeOpen = window.open.bind(window);
  window.open = ((url?: string | URL, target?: string, features?: string) => {
    if (editEnabled && url) {
      const path = sameOriginPath(typeof url === "string" ? url : url.toString());
      if (path !== null) {
        navigateInFrame(path);
        return null;
      }
    }
    return nativeOpen(url as string, target, features);
  }) as typeof window.open;
}

export interface InitCmsOptions {
  allowedParentOrigins: string[];
  enableParam?: string;
}

export function initCmsBridge(options: InitCmsOptions): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  allowedOrigins = options.allowedParentOrigins;

  const param = options.enableParam ?? "cmsEdit";
  const params = new URLSearchParams(window.location.search);
  if (isFramed() && params.get(param) === "1") {
    editEnabled = true;
  }

  window.addEventListener("message", (event: MessageEvent) => {
    if (!originAllowed(event.origin)) return;
    const data = event.data as {
      source?: string;
      type?: string;
      section?: string;
      data?: unknown;
      on?: boolean;
      path?: string;
      lang?: string;
    };
    if (!data || data.source !== PARENT_SOURCE) return;

    switch (data.type) {
      case "cms:editmode":
        editEnabled = !!data.on;
        // Re-announce so the parent has pages/location even if it attached its
        // listener after our initial broadcast — for preview (on:false) too.
        if (cachedPages) postToParent("cms:pages", { pages: cachedPages });
        if (cachedPath) postToParent("cms:location", { path: cachedPath });
        emit();
        break;
      case "cms:data":
        if (typeof data.section === "string") {
          overrides.set(data.section, data.data);
          emit();
        }
        break;
      case "cms:navigate":
        if (typeof data.path === "string") navigateInFrame(data.path);
        break;
      case "cms:setLanguage":
        if (typeof data.lang === "string" && languageSetter) languageSetter(data.lang);
        break;
      default:
        break;
    }
  });

  installNavigationGuards();

  postToParent("cms:ready", {});
  if (editEnabled) emit();
}
